/**
 * Shared helpers for routing unauthenticated users to /login and back.
 *
 * Used by both the RequireAuth route guard and the axios 401 interceptor so
 * the `returnTo` round-trip behaves identically no matter which layer fires.
 */

/** localStorage key holding the current user (token itself is an HttpOnly cookie). */
export const AUTH_USER_KEY = 'auth_user'

/**
 * Validate a candidate `returnTo` path. Only same-origin absolute paths are
 * allowed — rejects external URLs, protocol-relative `//host`, and loops back
 * to /login. Returns the safe path or null.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null
  // Must be a root-relative path, not protocol-relative (`//`, `/\`) or absolute URL.
  if (!raw.startsWith('/')) return null
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null
  // Never send the user back to the login page itself.
  if (raw === '/login' || raw.startsWith('/login?') || raw.startsWith('/login/')) return null
  return raw
}

/**
 * Build the login path, preserving where the user was headed as a `returnTo`
 * query param when it is a safe internal destination.
 */
export function loginPathWithReturn(currentPathWithSearch: string): string {
  const clean = sanitizeReturnTo(currentPathWithSearch)
  return clean ? `/login?returnTo=${encodeURIComponent(clean)}` : '/login'
}
